import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { GetCurrentEmployeeResponseOrganization } from '@me-fit-mono/typings';
import { Feedback } from 'src/app/decorators/feedback-decorator';
import { OrganizationStateService } from '../state/organization.state.service';
import { TranslationKey } from '../typings/english-translation-keys';

export type BillingStatus = 'trialing' | 'active';

export interface BillingState {
  status: BillingStatus;
  daysLeft?: number;
}

export interface BillingInfoForUi {
  title: TranslationKey;
  message: TranslationKey;
  buttonText: TranslationKey;
}

export type BillingDisplayContext = 'menu' | 'banner';

export interface GetBillingDisplayInfoOptions {
  displayContext?: BillingDisplayContext;
}

@Injectable({
  providedIn: 'root'
})
export class BillingService {
  constructor(
    private $organizationState: OrganizationStateService,
    private $translate: TranslateService
  ) {}

  /**
   * Check if organization has already used their trial period
   * Returns true if subscription was ever created
   */
  hasUsedTrial(org: GetCurrentEmployeeResponseOrganization): boolean {
    return !!org.billingInformation?.subscriptionId;
  }



  /**
   * Check if organization has good billing status (active or trialing)
   * Returns true if the organization can access paid features
   *
   * Also checks if employee count exceeds seat count
   */
  hasGoodBillingStatus(org: GetCurrentEmployeeResponseOrganization): boolean {
    // Check exemption FIRST - bypasses all other checks
    if (org.isBillingExempt) {
      return true;
    }

    const state = this.getBillingState(org);
    const hasActiveSubscription = Boolean(state && (state.status === 'trialing' || state.status === 'active'));

    // Check if employees exceed paid seats
    if (hasActiveSubscription && org.billingInformation?.isOverCapacity) {
      return false; // Over capacity - send to billing gate
    }

    return hasActiveSubscription;
  }

  /**
   * Calculates the current billing state from organization data
   * This is the single source of truth for billing state logic
   */
  getBillingState(org: GetCurrentEmployeeResponseOrganization): BillingState | null {
    const billing = org.billingInformation;
    if (!billing?.subscriptionId) {
      return null;
    }

    // Trial is active
    if (billing.subscriptionStatus === 'trialing' && billing.currentPeriodEnd) {
      const daysLeft = Math.ceil(
        (new Date(billing.currentPeriodEnd).getTime() - Date.now()) / (1000 * 60 * 60 * 24)
      );

      return {
        status: 'trialing',
        daysLeft
      };
    }

    // Active subscription
    if (billing.subscriptionStatus === 'active') {
      return {
        status: 'active'
      };
    }

    return null;
  }

  /**
   * Gets billing display info for both banner and menu
   * Returns null when no billing info should be displayed
   * @param org - Organization data
   * @param options - Display options
   */
  getBillingDisplayInfo(
    org: GetCurrentEmployeeResponseOrganization,
    options: GetBillingDisplayInfoOptions = {}
  ): BillingInfoForUi | null {
    // Hide UI for exempt organizations
    if (org.isBillingExempt) {
      return null;
    }

    const state = this.getBillingState(org);
    if (!state) {
      return null;
    }

    const shouldHide = !this.hasGoodBillingStatus(org);

    if (shouldHide) {
      return null;
    }

    // Trial is active - show throughout entire trial
    if (state.status === 'trialing' && state.daysLeft !== undefined) {
      const hasPayment = org.billingInformation?.hasPaymentMethod;
      const isBanner = options.displayContext === 'banner';

      // Banner: hide if payment method already added
      if (isBanner && hasPayment) {
        return null;
      }

      const message = state.daysLeft > 1 ? this.$translate.instant('billing.menu.trial.days.left', { days: state.daysLeft }) : this.$translate.instant('billing.menu.trial.last.day');

      // Banner without payment: show "Add payment" prompt
      // Menu or banner with payment: show "Manage"
      const buttonText = !hasPayment
        ? this.$translate.instant('billing.button.add.payment.method')
        : this.$translate.instant('billing.menu.manage.button');

      return {
        title: this.$translate.instant('billing.menu.trial.active'),
        message,
        buttonText
      };
    }

    return null;
  }

  @Feedback({
    i18nFailKey: 'billing.checkout.fail'
  })
  async redirectToCheckout(successUrl?: string) {
    const response = await this.$organizationState.getCheckoutLink.call({
      request: {
        successUrl
      }
    });
    if (response?.url) {
      window.location.href = response.url;
    }
    return response;
  }

  @Feedback({
    i18nFailKey: 'billing.portal.open.fail',
  })
  async manageBilling(returnUrl?: string) {
    const response = await this.$organizationState.getBillingPortal.call({
      request: {
        returnUrl
      }
    });
    if (response?.url) {
      window.location.href = response.url;
    }
    return response;
  }
}
